import { col, type Shape } from './base.ts';

export const RISK_SCENARIO_META: Shape = [
  col('scenarioName', 'unitless', 'Name of the optimization scenario.'),
  col('runID', 'unitless', 'Identifier for this optimization run.'),
];

/**
 * The flow-assurance risk-profile measure family shared by every NetworkRisk
 * source: the overall aggregated risk plus a per-hazard
 * `{risk, max}` pair for each of the eight hazards (asphaltene, corrosion,
 * erosion, EVR, hydrate, PE5 stability, scale, wax). All five NetworkRisk
 * schemas (String/Pipeline current-state, their scenario variants, and the
 * entity-grain `NetworkRisk_OptimizationScenarios`) carry this IDENTICAL
 * 17-timeseries set, so ONE shape fronts every grain — only the kept grain
 * relation (and the scenario meta) differs.
 *
 * The source measure columns are bare (no prefix) but irregularly named at the
 * hazard level (`asphalteneDTMax`, `corrosionRateMax`, `hydrateDTMax`,
 * `scaleSIMax`/`scaleSIRisk`, `waxDTMax`), so this shape FLATTENS them to clean
 * canonical hazard names (`asphalteneMax`, `corrosionMax`, `hydrateMax`,
 * `scaleMax`/`scaleRisk`, `waxMax`) — the only NetworkRisk group that emits
 * `(rename …)` ops. The `suffix` of each entry is the VERBATIM source column;
 * every one must resolve (a typo silently degrades to `unknownCol`).
 */
export const RISK_PROFILE: Shape = [
  col('aggregatedRisk', 'unitless', 'Overall aggregated flow-assurance risk level.'),
  col('asphalteneRisk', 'unitless', 'Asphaltene deposition risk level.'),
  col('asphalteneMax', 'temperatureDelta', 'Maximum asphaltene onset temperature delta.', {
    suffix: 'asphalteneDTMax',
  }),
  col('corrosionRisk', 'unitless', 'Corrosion risk level.'),
  col('corrosionMax', 'unitless', 'Maximum corrosion rate across the entity.', { suffix: 'corrosionRateMax' }),
  col('erosionRisk', 'unitless', 'Erosion risk level.'),
  col('erosionMax', 'unitless', 'Maximum erosion rate.'),
  col('evrRisk', 'unitless', 'Erosion-velocity-ratio risk level.'),
  col('evrMax', 'unitless', 'Maximum erosion velocity ratio.'),
  col('hydrateRisk', 'unitless', 'Hydrate formation risk level.'),
  col('hydrateMax', 'temperatureDelta', 'Maximum hydrate temperature delta.', { suffix: 'hydrateDTMax' }),
  col('pe5StabilityRisk', 'unitless', 'PE5 flow-stability risk level.'),
  col('pe5StabilityMax', 'unitless', 'Maximum PE5 stability indicator.'),
  col('scaleRisk', 'unitless', 'Scale deposition risk level.', { suffix: 'scaleSIRisk' }),
  col('scaleMax', 'unitless', 'Maximum scale saturation index.', { suffix: 'scaleSIMax' }),
  col('waxRisk', 'unitless', 'Wax deposition risk level.'),
  col('waxMax', 'temperatureDelta', 'Maximum wax deposition temperature delta.', { suffix: 'waxDTMax' }),
];
/**
 * Per-scenario gas-lift + power extras on `NetworkOutputs_Field_Agent`,
 * carried only by the `systemOptimized` scenario family on top of the standard
 * `PHASE_RATES` triplet. Resolved against the `systemOptimized` prefix, so the
 * `suffix` here is the SCENARIO-LOCAL tail (`GasLiftRate`/`Power`).
 */
